import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { buildNewCustomerUrl } from '../../utils/customerCreateReturn'
import { filterCustomersBySearch } from '../../utils/customerSearch'
import type { CustomerDto } from '../../types/customer'
import { cx, formLabelClass } from '../UI'
import { CustomerStageBadge } from './CustomerStageBadge'

type ControlSize = 'sm' | 'md' | 'lg'

const controlHeightClass: Record<ControlSize, string> = {
  sm: 'h-[38px]',
  md: 'h-[46px]',
  lg: 'h-[54px]',
}

const selectButtonSizeClass: Record<ControlSize, string> = {
  sm: 'rounded-xl py-2 pl-3 text-sm',
  md: 'rounded-xl py-2.5 pl-3 text-base',
  lg: 'rounded-2xl py-3.5 pl-4 text-base',
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" />
    </svg>
  )
}

function ClearIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path strokeLinecap="round" d="M6 6l8 8M14 6l-8 8" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden
    >
      <circle cx="9" cy="9" r="5.5" />
      <path strokeLinecap="round" d="m13.5 13.5 3.5 3.5" />
    </svg>
  )
}

function customerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

function formatOffersCount(n: number): string {
  if (n === 0) return 'Καμία προσφορά'
  if (n === 1) return '1 προσφορά'
  return `${n} προσφορές`
}

function customerSecondaryLine(customer: CustomerDto): string {
  const parts: string[] = []
  if (customer.phone?.trim()) parts.push(customer.phone.trim())
  if (customer.email?.trim()) parts.push(customer.email.trim())
  if (parts.length === 0 && customer.address?.trim()) {
    const line = customer.address.trim().replace(/\s+/g, ' ')
    parts.push(line.length > 52 ? `${line.slice(0, 49)}…` : line)
  }
  if (parts.length === 0) return 'Χωρίς τηλέφωνο ή email'
  return parts.join(' · ')
}

function CustomerAvatar({ name }: { name: string }) {
  return (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-800 dark:bg-violet-950/80 dark:text-violet-200"
      aria-hidden
    >
      {customerInitials(name)}
    </span>
  )
}

function CustomerOptionRow({
  customer,
  selected,
  onSelect,
}: {
  customer: CustomerDto
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={cx(
        'flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition',
        selected
          ? 'bg-violet-50 ring-1 ring-violet-200/80 dark:bg-violet-950/40 dark:ring-violet-800/60'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/80',
      )}
    >
      <CustomerAvatar name={customer.name} />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-zinc-900 dark:text-white">
            {customer.name}
          </span>
          <CustomerStageBadge stage={customer.stage} className="!px-2 !py-0.5 !text-[10px]" />
        </span>
        <span className="mt-0.5 block truncate text-sm text-zinc-600 dark:text-zinc-400">
          {customerSecondaryLine(customer)}
        </span>
        <span className="mt-1 block text-xs text-zinc-500 dark:text-zinc-500">
          {formatOffersCount(customer.offersCount)}
          {customer.vatNumber?.trim() ? (
            <span className="text-zinc-400"> · ΑΦΜ {customer.vatNumber.trim()}</span>
          ) : null}
        </span>
      </span>
      {selected ? (
        <span
          className="shrink-0 pt-1 text-sm font-bold text-violet-600 dark:text-violet-300"
          aria-hidden
        >
          ✓
        </span>
      ) : null}
    </button>
  )
}

export type CustomerSelectProps = {
  id?: string
  value: string
  onChange: (customerId: string) => void
  customers?: CustomerDto[]
  loading?: boolean
  error?: Error | null
  disabled?: boolean
  clearable?: boolean
  placeholder?: string
  controlSize?: ControlSize
  className?: string
  label?: string
  hint?: React.ReactNode
  /** Προαιρετικό path επιστροφής· αλλιώς η τρέχουσα σελίδα. */
  createCustomerReturnTo?: string
  onClear?: () => void
}

export function CustomerSelect({
  id,
  value,
  onChange,
  customers,
  loading = false,
  error = null,
  disabled = false,
  clearable = false,
  placeholder = 'Επίλεξε πελάτη…',
  controlSize = 'lg',
  className,
  label = 'Πελάτης',
  hint,
  createCustomerReturnTo,
  onClear,
}: CustomerSelectProps) {
  const location = useLocation()
  const newCustomerUrl = useMemo(() => {
    const returnTo =
      createCustomerReturnTo ??
      `${location.pathname}${location.search}`
    return buildNewCustomerUrl(returnTo)
  }, [createCustomerReturnTo, location.pathname, location.search])
  const sorted = useMemo(
    () =>
      [...(customers ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'el'),
      ),
    [customers],
  )
  const selected = useMemo(
    () => sorted.find((c) => c.id === value) ?? null,
    [sorted, value],
  )

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [popoverLayout, setPopoverLayout] = useState({
    top: 0,
    left: 0,
    width: 320,
    maxHeight: 360,
  })

  const filtered = useMemo(
    () => filterCustomersBySearch(sorted, search),
    [sorted, search],
  )

  const canClear = Boolean(clearable && value && !disabled && !loading)
  const isDisabled = disabled || loading

  function updatePopoverLayout() {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    const width = Math.max(rect.width, 300)
    const margin = 8
    const left = Math.min(
      Math.max(rect.left, margin),
      window.innerWidth - width - margin,
    )
    const availableBelow = window.innerHeight - rect.bottom - margin
    const maxHeight = Math.min(400, Math.max(160, availableBelow))
    setPopoverLayout({ top: rect.bottom + margin, left, width, maxHeight })
  }

  function openDropdown() {
    if (isDisabled) return
    setSearch('')
    updatePopoverLayout()
    setIsOpen(true)
  }

  function closeDropdown() {
    setIsOpen(false)
    setSearch('')
  }

  function selectCustomer(customerId: string) {
    onChange(customerId)
    closeDropdown()
  }

  function handleClear() {
    onChange('')
    onClear?.()
    closeDropdown()
  }

  useEffect(() => {
    if (!isOpen) return
    const t = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    function closeOnOutside(e: PointerEvent) {
      const target = e.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        closeDropdown()
      }
    }
    document.addEventListener('pointerdown', closeOnOutside)
    return () => document.removeEventListener('pointerdown', closeOnOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    updatePopoverLayout()
    window.addEventListener('resize', updatePopoverLayout)
    window.addEventListener('scroll', updatePopoverLayout, true)
    return () => {
      window.removeEventListener('resize', updatePopoverLayout)
      window.removeEventListener('scroll', updatePopoverLayout, true)
    }
  }, [isOpen])

  const triggerLabel = loading
    ? 'Φόρτωση πελατών…'
    : selected
      ? selected.name
      : placeholder

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={id} className={formLabelClass}>
          {label}
        </label>
      ) : null}

      <div
        ref={rootRef}
        className="relative"
        onKeyDown={(e) => {
          if (e.key === 'Escape') closeDropdown()
        }}
      >
        <button
          ref={buttonRef}
          id={id}
          type="button"
          disabled={isDisabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => (isOpen ? closeDropdown() : openDropdown())}
          className={cx(
            'w-full border border-zinc-200 bg-white text-left shadow-sm outline-none transition hover:border-zinc-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/25 disabled:cursor-not-allowed disabled:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900/80 dark:hover:border-zinc-600 dark:focus:border-violet-400 dark:focus:ring-violet-400/20 dark:disabled:bg-zinc-800/70',
            selectButtonSizeClass[controlSize],
            selected ? 'min-h-[58px] py-2.5' : controlHeightClass[controlSize],
            'relative flex items-center gap-3 rounded-xl pl-3',
            canClear ? 'pr-16' : 'pr-10',
          )}
        >
          {selected ? (
            <>
              <CustomerAvatar name={selected.name} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-zinc-900 dark:text-white">
                  {selected.name}
                </span>
                <span className="block truncate text-sm text-zinc-600 dark:text-zinc-400">
                  {customerSecondaryLine(selected)}
                </span>
              </span>
            </>
          ) : (
            <span className="min-w-0 flex-1 truncate text-zinc-500 dark:text-zinc-400">
              {triggerLabel}
            </span>
          )}
          {canClear ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Καθαρισμός επιλογής"
              title="Καθαρισμός"
              onClick={(e) => {
                e.stopPropagation()
                handleClear()
              }}
              className="absolute right-9 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 ring-1 ring-zinc-200 transition hover:bg-red-50 hover:text-red-600 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-zinc-700 dark:hover:bg-red-950/50 dark:hover:text-red-300"
            >
              <ClearIcon className="h-3.5 w-3.5" />
            </span>
          ) : null}
          <span className="pointer-events-none absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-zinc-600 dark:text-zinc-300">
            <ChevronDownIcon
              className={cx('h-5 w-5 transition', isOpen && 'rotate-180')}
            />
          </span>
        </button>

        {isOpen && typeof document !== 'undefined'
          ? createPortal(
              <div
                ref={popoverRef}
                role="listbox"
                style={{
                  top: popoverLayout.top,
                  left: popoverLayout.left,
                  width: popoverLayout.width,
                  maxHeight: popoverLayout.maxHeight,
                }}
                className="fixed z-[9999] flex flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
              >
                <div className="border-b border-zinc-100 p-2 dark:border-zinc-800">
                  <div className="relative">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <input
                      ref={searchRef}
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Αναζήτηση ονόματος, τηλεφώνου, email…"
                      className="w-full rounded-xl border border-zinc-200 bg-zinc-50 py-2.5 pl-9 pr-3 text-sm text-zinc-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20 dark:border-zinc-700 dark:bg-zinc-950/60 dark:text-zinc-100 dark:focus:border-violet-400"
                      aria-label="Αναζήτηση πελατών"
                    />
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                  {loading ? (
                    <p className="px-3 py-6 text-center text-sm text-zinc-500">
                      Φόρτωση…
                    </p>
                  ) : sorted.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-zinc-600 dark:text-zinc-400">
                      <p>Δεν υπάρχουν πελάτες.</p>
                      {newCustomerUrl ? (
                        <Link
                          to={newCustomerUrl}
                          className="mt-2 inline-block font-semibold text-violet-600 hover:underline dark:text-violet-400"
                          onClick={() => closeDropdown()}
                        >
                          Πρόσθεσε πελάτη
                        </Link>
                      ) : null}
                    </div>
                  ) : filtered.length === 0 ? (
                    <p className="px-3 py-6 text-center text-sm text-zinc-500">
                      Δεν βρέθηκε πελάτης για «{search.trim()}».
                    </p>
                  ) : (
                    filtered.map((c) => (
                      <CustomerOptionRow
                        key={c.id}
                        customer={c}
                        selected={c.id === value}
                        onSelect={() => selectCustomer(c.id)}
                      />
                    ))
                  )}
                </div>

                {newCustomerUrl && sorted.length > 0 ? (
                  <div className="border-t border-zinc-100 px-3 py-2 dark:border-zinc-800">
                    <Link
                      to={newCustomerUrl}
                      className="text-xs font-semibold text-violet-600 hover:underline dark:text-violet-400"
                      onClick={() => closeDropdown()}
                    >
                      + Νέος πελάτης
                    </Link>
                  </div>
                ) : null}
              </div>,
              document.body,
            )
          : null}
      </div>

      {error ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          Δεν φορτώθηκαν οι πελάτες. Ελέγξτε το API.
        </p>
      ) : null}
      {hint}
    </div>
  )
}
